import { useState, useEffect, useMemo, useRef } from "react";
import styles from "./index.module.scss";
import { Row } from "react-bootstrap";
import ContentLoader from "react-content-loader";
import NavigationTabs from "./navigation-tabs";
import PageHeader from "./page-header";
import PageDescription from "./page-description";
import IPWhitelistForm from "./ip-whitelist-form";
import TrustedIPRanges from "./trusted-ip-ranges";
import { CustomDeleteModal } from "../../DeleteModal/DeleteModal";
import useActions from "../../../hooks/service-actions.hook";
import { NetworkAccessEffectActions } from "../../../common/services/network-access/network-access.service";
import { useEntity } from "../../../hooks/use-entity.hook";
import { Entities } from "../../../common/store/entities/entities.enum";
import useLoaders from "../../../hooks/use-loaders.hook";
import { Loaders } from "../../../common/store/loaders/loaders.enum";
import { CallToast } from "../../PriceCalculator/modals/CustomToast";

const NetworkAccessPage = () => {
  const { getAllIPAddresses, addIPAddress, updateIPAddress, deleteIPAddress } = useActions(NetworkAccessEffectActions);
  const { entityData: networkAccessData } = useEntity({ entityType: Entities.NetworkAccess });
  const { isLoading: isIPAddressesLoading } = useLoaders({ loaders: [Loaders.GetAllIPAddressesLoader] });
  const { isLoading: isAddingIPAddress } = useLoaders({ loaders: [Loaders.AddIPAddressLoader] });
  const { isLoading: isUpdatingIPAddress } = useLoaders({ loaders: [Loaders.UpdateIPAddressLoader] });
  const { isLoading: isDeletingIPAddress } = useLoaders({ loaders: [Loaders.DeleteIPAddressLoader] });
  
  const [ipInput, setIpInput] = useState("");
  const [error, setError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const shouldRefreshAfterAdd = useRef(false);
  const shouldRefreshAfterUpdate = useRef(false);
  const shouldRefreshAfterDelete = useRef(false);
  const isDeletingAll = useRef(false);
  const deleteAllCount = useRef(0);

  const ipAddresses = useMemo(() => {
    if (!networkAccessData || typeof networkAccessData !== 'object') {
      return [];
    }
    if (!networkAccessData.IPAddresses || !Array.isArray(networkAccessData.IPAddresses)) {
      return [];
    }
    return networkAccessData.IPAddresses.filter((ip) => ip && ip.Id && ip.IPAddress);
  }, [networkAccessData]);

  useEffect(() => {
    getAllIPAddresses();
  }, [getAllIPAddresses]);

  useEffect(() => {
    if (shouldRefreshAfterAdd.current && !isAddingIPAddress) {
      getAllIPAddresses();
      shouldRefreshAfterAdd.current = false;
    }
  }, [isAddingIPAddress, getAllIPAddresses]);

  useEffect(() => {
    if (shouldRefreshAfterUpdate.current && !isUpdatingIPAddress) {
      getAllIPAddresses();
      shouldRefreshAfterUpdate.current = false;
    }
  }, [isUpdatingIPAddress, getAllIPAddresses]);

  useEffect(() => {
    if (shouldRefreshAfterDelete.current && !isDeletingIPAddress) {
      getAllIPAddresses();
      setShowDeleteModal(null);
      
      if (isDeletingAll.current) {
        CallToast("All IP addresses deleted successfully", true, false, "v2style");
        isDeletingAll.current = false;
        deleteAllCount.current = 0;
      }
      
      shouldRefreshAfterDelete.current = false;
    }
  }, [isDeletingIPAddress, getAllIPAddresses]);

  const navigationTabs = [
    { label: "Company Settings", active: false, disabled: true },
    { label: "Members and Access", active: false, disabled: true },
    { label: "Tags", active: false, disabled: true },
    { label: "Statuses", active: false, disabled: true },
    { label: "Paid Plans", active: false, disabled: true },
    { label: "Network Access", active: true, disabled: false },
  ];

  const validateIpAddress = (ip) => {
    const ipv4Pattern = /^(\d{1,3}\.){3}\d{1,3}$/;
    const cidrPattern = /^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/;
    const rangePattern = /^(\d{1,3}\.){3}\d{1,3}-(\d{1,3}\.){3}\d{1,3}$/;

    if (!ip || ip.trim() === "") {
      return "IP address cannot be empty";
    }

    const trimmedIp = ip.trim();

    if (ipv4Pattern.test(trimmedIp)) {
      const parts = trimmedIp.split(".");
      for (const part of parts) {
        const num = parseInt(part, 10);
        if (num < 0 || num > 255) {
          return "Invalid IP address. Each octet must be between 0 and 255";
        }
      }
      return null;
    }

    if (cidrPattern.test(trimmedIp)) {
      const [ipPart, cidrPart] = trimmedIp.split("/");
      const parts = ipPart.split(".");
      for (const part of parts) {
        const num = parseInt(part, 10);
        if (num < 0 || num > 255) {
          return "Invalid IP address in CIDR notation";
        }
      }
      const cidr = parseInt(cidrPart, 10);
      if (cidr < 0 || cidr > 32) {
        return "CIDR prefix must be between 0 and 32";
      }
      return null;
    }

    if (rangePattern.test(trimmedIp)) {
      const [startIp, endIp] = trimmedIp.split("-");
      const startParts = startIp.trim().split(".");
      const endParts = endIp.trim().split(".");
      
      for (const part of [...startParts, ...endParts]) {
        const num = parseInt(part, 10);
        if (num < 0 || num > 255) {
          return "Invalid IP address in range";
        }
      }
      return null;
    }

    return "Invalid IP address format. Use IPv4 (e.g., 192.168.1.1), CIDR (e.g., 192.168.1.0/24), or range (e.g., 192.168.1.0-192.168.1.255)";
  };

  const handleAddIp = () => {
    const trimmedInput = ipInput.trim();
    
    setError("");

    if (!trimmedInput) {
      setError("IP address cannot be empty");
      return;
    }

    const validationError = validateIpAddress(trimmedInput);
    if (validationError) {
      setError(validationError);
      return;
    }

    const isDuplicate = ipAddresses.some(
      (ip) => ip.IPAddress && ip.IPAddress.toLowerCase() === trimmedInput.toLowerCase()
    );
    if (isDuplicate) {
      setError("This IP address is already in the list");
      return;
    }

    addIPAddress({
      IPAddress: trimmedInput,
      Description: "",
      IsActive: true,
    });

    shouldRefreshAfterAdd.current = true;

    setIpInput("");
    setError("");
  };

  const handleDeleteWrapper = (id) => {
    const isValidId = id !== undefined && id !== null && typeof id !== 'object' && (typeof id === 'number' || typeof id === 'string');
    
    setShowDeleteModal({
      show: true,
      deleteHeaderText: isValidId ? "Delete IP Address" : "Delete All IP Addresses",
      deleteBodyText: isValidId ? "Are you sure you want to delete this IP address?" : "Are you sure you want to delete all IP addresses?",
      deleteFooterText: "This action cannot be undone.",
      deleteButtonText: isValidId ? "Delete" : "Delete All",
      deleteButtonClass: "delete-btn",
      deleteFunction: () => {
        handleDelete(isValidId ? id : undefined);
      },
    });
  };

  const handleEdit = (id, newAddress) => {
    if (!newAddress) {
      return { success: false, error: "IP address cannot be empty" };
    }

    const trimmedAddress = newAddress.trim();
    
    const validationError = validateIpAddress(trimmedAddress);
    if (validationError) {
      return { success: false, error: validationError };
    }

    const isDuplicate = ipAddresses.some(
      (ip) => ip.Id !== id && ip.IPAddress && ip.IPAddress.toLowerCase() === trimmedAddress.toLowerCase()
    );
    if (isDuplicate) {
      return { success: false, error: "This IP address is already in the list" };
    }

    const ipAddress = ipAddresses.find((ip) => ip.Id === id);
    if (!ipAddress) {
      return { success: false, error: "IP address not found" };
    }

    updateIPAddress({
      Id: id,
      IPAddress: trimmedAddress,
      Description: ipAddress.Description || "",
      IsActive: ipAddress.IsActive !== undefined ? ipAddress.IsActive : true,
    });

    shouldRefreshAfterUpdate.current = true;

    return { success: true, error: null };
  };

  const handleDelete = (id) => {
    if (id === undefined || id === null) {
      if (ipAddresses.length === 0) {
        return;
      }
      
      isDeletingAll.current = true;
      deleteAllCount.current = ipAddresses.filter(ip => ip && ip.Id).length;
      
      ipAddresses.forEach((ip) => {
        if (ip && ip.Id) {
          deleteIPAddress({ ipId: ip.Id, suppressToast: true });
        }
      });
      shouldRefreshAfterDelete.current = true;
      return;
    }

    deleteIPAddress({ ipId: id });
    shouldRefreshAfterDelete.current = true;
  };

  const descriptionText =
    "Define the IP ranges that are allowed to access your HighGround " +
    "account. Only users connecting from these trusted networks will be " +
    "granted access — all other locations will be denied. Use this to limit " +
    "exposure, prevent unauthorized logins, and streamline access for " +
    "internal teams.";

  return (
    <div className={styles.container}>
      <PageHeader title="Network Access" />

      <PageDescription text={descriptionText} />

      <IPWhitelistForm
        ipInput={ipInput}
        onInputChange={(value) => {
          setIpInput(value);
          setError("");
        }}
        onAdd={handleAddIp}
        error={error}
        isLoading={isAddingIPAddress}
      />

      {isIPAddressesLoading && ipAddresses.length === 0 ? (
        <SecurityStackReportLoader />
      ) : (
        <TrustedIPRanges
          ipAddresses={ipAddresses}
          onEdit={handleEdit}
          onDelete={handleDeleteWrapper}
          isUpdatingIPAddress={isUpdatingIPAddress}
        />
      )}

<CustomDeleteModal
        type={showDeleteModal?.type}    
        show={showDeleteModal?.show}
        isLoading={isDeletingIPAddress}
        deleteHeaderText={showDeleteModal?.deleteHeaderText}
        deleteBodyText={showDeleteModal?.deleteBodyText}
        deleteFooterText={showDeleteModal?.deleteFooterText}
        deleteButtonText={showDeleteModal?.deleteButtonText}
        deleteButtonClass={showDeleteModal?.deleteButtonClass}
        hideModal={()=>{
          if (!isDeletingIPAddress) {
            setShowDeleteModal(null);
          }
        }}
        deleteFunction={()=>{
          if (showDeleteModal?.deleteFunction) {
            showDeleteModal.deleteFunction();
          }
        }}
        deleteParams={showDeleteModal?.deleteParams}
      />
    </div>
  );
};

export default NetworkAccessPage;

export const SecurityStackReportLoader = (props) => {
  return (
    <Row style={{ paddingTop: "2%" }}>
      <ContentLoader
        speed={2}
        width={'100%'}
        height={750}
        backgroundColor="#F6F8FB"
        foregroundColor="#ecebeb"
        {...props}
      >
        <rect x="2%" y="25" rx="12" ry="12" width="96%" height="50" />
        <rect x="2%" y="80" rx="12" ry="12" width="96%" height="50" />
        <rect x="2%" y="135" rx="12" ry="12" width="96%" height="50" />
        <rect x="2%" y="190" rx="12" ry="12" width="96%" height="50" />
        <rect x="2%" y="245" rx="12" ry="12" width="96%" height="50" />
        <rect x="2%" y="300" rx="12" ry="12" width="96%" height="50" />
        <rect x="2%" y="355" rx="12" ry="12" width="96%" height="50" />
        <rect x="2%" y="410" rx="12" ry="12" width="96%" height="50" />
        <rect x="2%" y="465" rx="12" ry="12" width="96%" height="50" />
        <rect x="2%" y="520" rx="12" ry="12" width="96%" height="50" />
        <rect x="2%" y="575" rx="12" ry="12" width="96%" height="50" />
        <rect x="2%" y="630" rx="12" ry="12" width="96%" height="50" />
        <rect x="2%" y="685" rx="12" ry="12" width="96%" height="50" />
      </ContentLoader>
    </Row>
  );
};
